import { combineReducers } from 'redux';

import advertsReducer from './adverts';
import * as fromAdverts from './adverts/selectors';
import agentsReducer from './agents';
import * as fromAgents from './agents/selectors';
import { createFlightsReducer } from './flights';
import * as fromFlights from './flights';
import loyaltyDiscountsReducer from './loyaltyDiscounts';
import * as fromLoyaltyDiscounts from './loyaltyDiscounts/selectors';
import { partnerMetadataReducer } from './partnerMetadata';
import * as fromPartnerMetadata from './partnerMetadata/selectors';
import paymentDiscountsReducer from './paymentDiscounts';
import * as fromPaymentDiscounts from './paymentDiscounts/selectors';
import { placesReducer } from './places';
import * as fromPlaces from './places/selectors';

import type { GetExitingItineraryId } from './flights';
import type { BookingPanelOptionAdvert } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/advertsBookingPanel';
import type { AgentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/agentsBookingPanel';
import type { CarrierBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/carriersBookingPanel';
import type {
  ErrorType,
  FilterStatusMapBookingPanel,
  ItineraryBookingPanel,
  ItineraryByIdBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';
import type { LegBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/legsBookingPanel';
import type { SegmentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/segmentsBookingPanel';
import type { LoyaltyDiscountsBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/loyaltyDiscountsBookingPanel';
import type { PartnerMetadataTrustLevelBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/partnerMetadataBookingPanel';
import type { PaymentDiscountsBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/paymentDiscountsBookingPanel';
import type { PlaceBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/placesBookingPanel';
import type { FlightBookingPanelEntitiesState } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/flightBookingPanel';
import type { StateType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { EntityId } from '@web-platform/shared-universal-types-utils/src/utils';

export const createFlightBookingPanelEntitiesReducer = (
  isTokenPanel: () => boolean,
  getExitingId: GetExitingItineraryId,
) =>
  combineReducers({
    adverts: advertsReducer,
    agents: agentsReducer,
    flights: createFlightsReducer(isTokenPanel, getExitingId),
    loyaltyDiscounts: loyaltyDiscountsReducer,
    partnerMetadata: partnerMetadataReducer,
    paymentDiscounts: paymentDiscountsReducer,
    places: placesReducer,
  });

// adverts
export const getBookingPanelAdvertBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): BookingPanelOptionAdvert | null => fromAdverts.bookingPanelAdvert(state.adverts);

// legs
export const flightLegBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): LegBookingPanel => fromFlights.leg(state.flights, id);
export const flightAllLegsBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): Record<string, LegBookingPanel> => fromFlights.allLegs(state.flights);

// carriers
export const flightCarrierBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: number,
): CarrierBookingPanel => fromFlights.carrier(state.flights, id);
export const flightAllCarriersBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): Record<number, CarrierBookingPanel> => fromFlights.allCarriers(state.flights);

// segments
export const flightSegmentBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): SegmentBookingPanel => fromFlights.segment(state.flights, id);
export const flightAllSegmentsBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): Record<string, SegmentBookingPanel> => fromFlights.allSegments(state.flights);

// places
export const placeByEntityIdBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  entityId: EntityId,
): PlaceBookingPanel => fromPlaces.getByEntityId(state.places, entityId);
export const allPlacesByEntityIdBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): Record<EntityId, PlaceBookingPanel> => fromPlaces.allByEntityId(state.places);

// agents
export const agentBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): AgentBookingPanel => fromAgents.get(state.agents, id);
export const allAgentsBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): Record<string, AgentBookingPanel> => fromAgents.getAll(state.agents);

// itineraries
export const flightItineraryBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): ItineraryBookingPanel => fromFlights.itinerary(state.flights, id);
export const flightAllItinerariesBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): ItineraryByIdBookingPanel => fromFlights.allItineraries(state.flights);
export const getItineraryFilterStatusMapBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): FilterStatusMapBookingPanel[] => fromFlights.getItineraryFilterStatusMap(state.flights, id);
export const getItineraryErrorBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): ErrorType | undefined => fromFlights.getItineraryError(state.flights, id);

// partnerMetadata
export const getPartnerMetadataBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): PartnerMetadataTrustLevelBookingPanel => fromPartnerMetadata.get(state.partnerMetadata, id);
export const getAllPartnerMetadataBookingPanel = (
  state: FlightBookingPanelEntitiesState,
): Record<string, PartnerMetadataTrustLevelBookingPanel> =>
  fromPartnerMetadata.getAll(state.partnerMetadata);

// loyaltyDiscounts
export const getLoyaltyDiscountsForItineraryBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): LoyaltyDiscountsBookingPanel | undefined => fromLoyaltyDiscounts.get(state.loyaltyDiscounts, id);

// paymentDiscounts
export const getPaymentDiscountsForItineraryBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): PaymentDiscountsBookingPanel | undefined => fromPaymentDiscounts.get(state.paymentDiscounts, id);
export const getAvailablePaymentDiscountsForItineraryBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromPaymentDiscounts.getAvailable(state.paymentDiscounts, id);
export const getFilteredPaymentDiscountsForItineraryBookingPanel = (
  state: FlightBookingPanelEntitiesState,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromPaymentDiscounts.getFilteredPaymentDiscounts(state.paymentDiscounts, id);
export const getSelectedPaymentMethodFilter = (
  state: FlightBookingPanelEntitiesState,
): string[] | undefined =>
  fromPaymentDiscounts.getSelectedPaymentMethodFilter(state.paymentDiscounts);
export const getPaymentDiscountTooltipDismissed = (
  state: FlightBookingPanelEntitiesState,
): boolean => fromPaymentDiscounts.getPaymentDiscountTooltipDismissed(state.paymentDiscounts);
export const getPaymentDiscountNames = (
  state: FlightBookingPanelEntitiesState,
  id: string,
  enablePaymentDiscounts: boolean,
): string[] =>
  fromPaymentDiscounts.getPaymentDiscountNames(state.paymentDiscounts, id, enablePaymentDiscounts);

export const getEntitiesBookingPanelSlice = (state: StateType) =>
  state.shared.flightBookingPanel.entities;
